/**
 * Endpoint Details Page
 *
 * Displays all requests for a specific API endpoint with their response details.
 * Accessible via clicking on an endpoint in the analytics ResponseTimesTable.
 */

import { useState, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, RefreshCw, Loader2, AlertCircle } from 'lucide-react';
import { subHours, subDays } from 'date-fns';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { AuditLogsTable } from '@/components/audit-logs-table';
import { PaginationControls } from '@/components/pagination-controls';
import { useAuditLogs, useAuditLogSummary } from '@/hooks/use-audit-logs';
import { ANALYTICS_TIME_RANGE_LABELS, type AnalyticsTimeRange } from '@/types/analytics';
import type { AuditLogFilters } from '@/types/audit-log';

/**
 * Get date range from time range selection
 */
function getDateRangeFromTimeRange(timeRange: AnalyticsTimeRange): {
  startDate: string;
  endDate: string;
} {
  const now = new Date();
  let startDate: Date;

  switch (timeRange) {
    case '1h':
      startDate = subHours(now, 1);
      break;
    case '24h':
      startDate = subHours(now, 24);
      break;
    case '7d':
      startDate = subDays(now, 7);
      break;
    case '30d':
      startDate = subDays(now, 30);
      break;
    case '90d':
      startDate = subDays(now, 90);
      break;
    default:
      startDate = subHours(now, 24);
  }

  return {
    startDate: startDate.toISOString(),
    endDate: now.toISOString(),
  };
}

/**
 * Endpoint Details Page Component
 */
export function EndpointDetailsPage() {
  const { endpoint: encodedEndpoint } = useParams<{ endpoint: string }>();
  const navigate = useNavigate();

  const [timeRange, setTimeRange] = useState<AnalyticsTimeRange>('24h');
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(50);

  // Decode the endpoint from URL params
  const decodedEndpoint = useMemo(() => {
    if (!encodedEndpoint) return '';
    try {
      return decodeURIComponent(encodedEndpoint);
    } catch {
      return encodedEndpoint;
    }
  }, [encodedEndpoint]);
  const dateRange = useMemo(() => getDateRangeFromTimeRange(timeRange), [timeRange]);

  // Build filters with endpoint and time range
  const filters: AuditLogFilters = useMemo(() => {
    return {
      endpoint: decodedEndpoint,
      ...dateRange,
      page,
      limit,
      sortBy: 'occurredAt',
      sortOrder: 'desc',
    };
  }, [decodedEndpoint, dateRange, page, limit]);
  const summaryFilters: AuditLogFilters = useMemo(
    () => ({
      endpoint: filters.endpoint,
      ...dateRange,
    }),
    [filters.endpoint, dateRange]
  );

  // Fetch rows and their capped total/severity summary in parallel.
  const { data, isLoading, isError, error, refetch } = useAuditLogs(filters);
  const {
    data: summary,
    isLoading: isSummaryLoading,
    isError: isSummaryError,
    error: summaryError,
    refetch: refetchSummary,
  } = useAuditLogSummary(summaryFilters);
  const totalPages = summary ? Math.ceil(summary.total / limit) : 1;

  /**
   * Handle back navigation
   */
  const handleBack = () => {
    navigate('/analytics');
  };

  /**
   * Handle time range change
   */
  const handleTimeRangeChange = (value: string) => {
    setTimeRange(value as AnalyticsTimeRange);
    setPage(1); // Reset to first page when time range changes
  };

  /**
   * Handle page change
   */
  const handlePageChange = (newPage: number) => {
    setPage(newPage);
  };

  /**
   * Handle limit change
   */
  const handleLimitChange = (newLimit: number) => {
    setLimit(newLimit);
    setPage(1); // Reset to first page when limit changes
  };

  /**
   * Handle retry
   */
  const handleRetry = () => {
    void Promise.all([refetch(), refetchSummary()]);
  };

  // No endpoint provided
  if (!decodedEndpoint) {
    return (
      <div className="p-4 sm:p-6 lg:p-8">
        <Alert variant="destructive">
          <AlertCircle className="h-4 w-4" />
          <AlertTitle>Invalid Endpoint</AlertTitle>
          <AlertDescription>
            No endpoint was specified. Please go back and select an endpoint from the analytics
            page.
          </AlertDescription>
          <Button variant="outline" size="sm" onClick={handleBack} className="mt-4">
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back to Analytics
          </Button>
        </Alert>
      </div>
    );
  }

  return (
    <div className="p-4 sm:p-6 lg:p-8">
      {/* Page Header */}
      <div className="mb-6 flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
        <div className="flex flex-col gap-2">
          {/* Back Button */}
          <Button variant="ghost" size="sm" onClick={handleBack} className="w-fit -ml-2">
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back to Analytics
          </Button>

          <div>
            <h1 className="text-2xl font-bold tracking-tight">Endpoint Details</h1>
            <p className="text-muted-foreground mt-1 font-mono text-sm bg-muted px-2 py-1 rounded w-fit">
              {decodedEndpoint}
            </p>
          </div>
        </div>

        {/* Time Range Selector */}
        <div className="flex items-center gap-2">
          <span className="text-sm text-muted-foreground">Time Range:</span>
          <Select value={timeRange} onValueChange={handleTimeRangeChange}>
            <SelectTrigger className="w-[180px]">
              <SelectValue placeholder="Select time range" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="1h">{ANALYTICS_TIME_RANGE_LABELS['1h']}</SelectItem>
              <SelectItem value="24h">{ANALYTICS_TIME_RANGE_LABELS['24h']}</SelectItem>
              <SelectItem value="7d">{ANALYTICS_TIME_RANGE_LABELS['7d']}</SelectItem>
              <SelectItem value="30d">{ANALYTICS_TIME_RANGE_LABELS['30d']}</SelectItem>
              <SelectItem value="90d">{ANALYTICS_TIME_RANGE_LABELS['90d']}</SelectItem>
            </SelectContent>
          </Select>
          <Button
            variant="outline"
            size="icon"
            onClick={handleRetry}
            disabled={isLoading}
            title="Refresh data"
          >
            <RefreshCw className={`h-4 w-4 ${isLoading ? 'animate-spin' : ''}`} />
          </Button>
        </div>
      </div>

      {/* Summary Stats */}
      {data && !isSummaryError && (
        <div className="mb-6 grid gap-4 sm:grid-cols-3">
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium">Total Requests</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">
                {isSummaryLoading ? '...' : (summary?.total.toLocaleString() ?? 0)}
                {summary?.isCapped && '+'}
              </div>
              <p className="text-xs text-muted-foreground mt-1">
                In the {ANALYTICS_TIME_RANGE_LABELS[timeRange].toLowerCase()}
              </p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium">Errors</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold text-red-600">
                {isSummaryLoading
                  ? '...'
                  : (
                      (summary?.severityCounts.error ?? 0) +
                      (summary?.severityCounts.critical ?? 0)
                    ).toLocaleString()}
                {summary?.isCapped && '+'}
              </div>
              <p className="text-xs text-muted-foreground mt-1">
                {summary?.isCapped
                  ? 'Error and critical events in the first 10,000 matched'
                  : 'Error and critical events'}
              </p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium">Current Page</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{data.pagination.page}</div>
              <p className="text-xs text-muted-foreground mt-1">
                of {totalPages}
                {summary?.isCapped && '+'} pages
              </p>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Loading State */}
      {isLoading && !data && (
        <div className="flex items-center justify-center h-64">
          <div className="text-center">
            <Loader2 className="h-8 w-8 animate-spin text-primary mx-auto mb-4" />
            <p className="text-muted-foreground">Loading requests...</p>
          </div>
        </div>
      )}

      {/* Error State */}
      {(isError || isSummaryError) && (
        <Alert variant="destructive">
          <AlertCircle className="h-4 w-4" />
          <AlertTitle>Error Loading Requests</AlertTitle>
          <AlertDescription>
            {error instanceof Error
              ? error.message
              : summaryError instanceof Error
                ? summaryError.message
                : 'Failed to load request data'}
          </AlertDescription>
          <Button variant="outline" size="sm" onClick={handleRetry} className="mt-4">
            <RefreshCw className="h-4 w-4 mr-2" />
            Retry
          </Button>
        </Alert>
      )}

      {/* Request List */}
      {!isError && (
        <Card>
          <CardHeader>
            <CardTitle>Requests</CardTitle>
            <CardDescription>
              All requests to this endpoint. Click any row for full details including
              request/response data.
            </CardDescription>
          </CardHeader>
          <CardContent className="p-0">
            <AuditLogsTable
              logs={data?.data || []}
              isLoading={isLoading}
              error={isError ? (error as Error) : null}
              onRetry={handleRetry}
            />
          </CardContent>
        </Card>
      )}

      {/* Pagination */}
      {data && summary && data.data.length > 0 && (
        <PaginationControls
          pagination={{
            total: summary.total,
            page: data.pagination.page,
            limit,
            totalPages,
          }}
          isTotalCapped={summary.isCapped}
          onPageChange={handlePageChange}
          onLimitChange={handleLimitChange}
          isLoading={isLoading}
        />
      )}
    </div>
  );
}
